import { Box } from "@mui/material";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { APP_COLOR, FONT_FAMILY } from "../../theme";

export interface QuotaMeterProps {
  usedMB: number;
  totalMB?: number;
  onUpsell?: () => void;
  upsellThreshold?: number;
}

const AMBER = "#E3A549";
const RED = "#E5575C";
const MUTED = "#889096";
const BORDER_SOFT = "#1c1f24";
const SURFACE = "#151718";

const upsellText = (pct: number): string => {
  if (pct >= 100) return "Storage full — get 5 GB";
  if (pct >= 80) return "Running low — get 5 GB";
  return "Sign up for 5 GB";
};

const QuotaMeter: React.FC<QuotaMeterProps> = ({
  usedMB,
  totalMB = 100,
  onUpsell,
  upsellThreshold = 60,
}) => {
  const pct = Math.min(100, Math.max(0, Math.round((usedMB / totalMB) * 100)));
  const tone = pct >= 100 ? "full" : pct >= 80 ? "warn" : "ok";
  const accent = tone === "full" ? RED : tone === "warn" ? AMBER : APP_COLOR;
  const tint =
    tone === "full"
      ? "rgba(229,87,92,0.16)"
      : tone === "warn"
        ? "rgba(227,165,73,0.16)"
        : "rgba(39,182,203,0.14)";
  const showUpsell = pct >= upsellThreshold;

  return (
    <Box
      sx={{
        background: SURFACE,
        border: `1px solid ${BORDER_SOFT}`,
        borderRadius: "12px",
        padding: "14px 15px",
        fontFamily: FONT_FAMILY,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            flex: "0 0 36px",
            borderRadius: "50%",
            background: tint,
            color: accent,
            display: "grid",
            placeItems: "center",
          }}
        >
          <CloudOutlinedIcon sx={{ fontSize: 19 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 13,
              color: "#BCBEC0",
              marginBottom: "8px",
            }}
          >
            <Box>
              <Box component="b" sx={{ color: "#FFFFFF", fontWeight: 600 }}>
                {usedMB} MB
              </Box>{" "}
              <Box component="span" sx={{ color: MUTED, fontVariantNumeric: "tabular-nums" }}>
                of {totalMB} MB
              </Box>
            </Box>
          </Box>
          <Box
            aria-hidden
            sx={{
              height: 6,
              borderRadius: "4px",
              background: "#2b2f36",
              overflow: "hidden",
            }}
          >
            <Box sx={{ display: "block", height: "100%", width: `${pct}%`, borderRadius: "4px", background: accent }} />
          </Box>
        </Box>
      </Box>
      {showUpsell && (
        <Box
          role="button"
          tabIndex={0}
          onClick={onUpsell}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onUpsell?.();
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "7px",
            marginTop: "12px",
            paddingTop: "12px",
            borderTop: `1px solid ${BORDER_SOFT}`,
            color: accent,
            fontSize: 12.5,
            fontWeight: 600,
            cursor: onUpsell ? "pointer" : "default",
          }}
        >
          {upsellText(pct)}
          <ArrowForwardIcon sx={{ fontSize: 14 }} />
        </Box>
      )}
    </Box>
  );
};

export default QuotaMeter;
